import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useGameData } from "./GameContext";
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { gameDetails } from "../App";
import { useLanguage } from "../context/LanguageContext";
import Navbar from "../components/Navbar";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const USERS_PER_PAGE = 10;

interface EvaluateYourself {
    VerbalIntelligence: number;
    Attention: number;
    VisualIntelligence: number;
    AnalyticalIntelligence: number;
    Memory: number;
}

interface LineJudgementQuestion {
    responseTime: number;
    isCorrect: boolean;
    leftLineLength: number;
    rightLineLength: number;
    chosenIndex: number;
}

interface LineJudgement {
    questions: LineJudgementQuestion[];
}

interface MatchToSimpleResult {
    responseTime: number;
    isCorrect: boolean;
}

interface MatchToSimpleGameManager {
    results: MatchToSimpleResult[];
}

interface MathTestQuestion {
    Question: string | null;
    Options: string[];
    SelectedAnswer: string;
    IsCorrect: boolean;
    TimeTaken: number;
}

interface SudokuGame {
    Difficulty: string;
    TotalTime: number;
    MoveCount: number;
    Completed: boolean;
}

interface StroopAnswer {
    question: string;
    textColor: string;
    selected: string;
    correct: boolean;
    time: number;
    scoreEarned: number;
    comboMultiplier: number;
}

interface Stroop {
    answers: StroopAnswer[];
    remainingLives: number;
    totalScore: number;
    maxCombo: number;
}

interface CorsiBlockResult {
    level: number;
    sequenceLength: number;
    timeTaken: number;
    isCorrect: boolean;
}

interface CorsiBlock {
    totalPlayTime: number;
    totalMoves: number;
    totalXP: number;
    results: CorsiBlockResult[];
}

interface WordTestResult {
    questionIndex: number;
    question: string;
    timeTaken: number;
    Correct: boolean;
    comboMultiplier: number;
}

interface WordTest {
    results: WordTestResult[];
}

const UsersPageGenix: React.FC = () => {
    const navigate = useNavigate();
    const { gameData } = useGameData();
    const { t } = useLanguage();
    const sessions = gameData["game6"] || [];
    const [currentPage, setCurrentPage] = useState(1);
    const [userIdFilter, setUserIdFilter] = useState<string>("");
    const [purchaseFilter, setPurchaseFilter] = useState<boolean | null>(null);
    const [confirmFilter, setConfirmFilter] = useState<boolean | null>(null);
    const [evaluateYourselfFilter, setEvaluateYourselfFilter] = useState<boolean | null>(null);
    const [lineJudgementFilter, setLineJudgementFilter] = useState<boolean | null>(null);
    const [matchToSimpleFilter, setMatchToSimpleFilter] = useState<boolean | null>(null);
    const [mathTestFilter, setMathTestFilter] = useState<boolean | null>(null);
    const [sudokuFilter, setSudokuFilter] = useState<boolean | null>(null);
    const [stroopFilter, setStroopFilter] = useState<boolean | null>(null);
    const [corsiBlockFilter, setCorsiBlockFilter] = useState<boolean | null>(null);
    const [wordTestFilter, setWordTestFilter] = useState<boolean | null>(null);

    const handleBack = () => {
        navigate("/");
    };

    const formatSessionTime = (seconds: number) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        let result = "";
        if (hours > 0) result += `${hours}${t("h")} `;
        if (minutes > 0) result += `${minutes}${t("m")} `;
        if (remainingSeconds > 0 || result === "") result += `${remainingSeconds}${t("s")}`;
        return result.trim();
    };

    const filteredSessions = sessions.filter((session) => {
        const hasEvaluateYourself = session.EvaluateYourself && Object.keys(JSON.parse(session.EvaluateYourself)).length > 0;
        const hasLineJudgement = session.LineJudgement && JSON.parse(session.LineJudgement).questions.length > 0;
        const hasMatchToSimple = session.MatchToSimpleGameManager && JSON.parse(session.MatchToSimpleGameManager).results.length > 0;
        const hasMathTest = session.MathTest && JSON.parse(session.MathTest).length > 0;
        const hasSudoku = session.Sudoku && JSON.parse(session.Sudoku).length > 0;
        const hasStroop = session.Stroop && JSON.parse(session.Stroop).answers.length > 0;
        const hasCorsiBlock = session.CorsiBlock && JSON.parse(session.CorsiBlock).results.length > 0;
        const hasWordTest = session.WordTest && JSON.parse(session.WordTest).results.length > 0;
        
        return (
            (!userIdFilter || session.userId.toLowerCase().includes(userIdFilter.toLowerCase())) &&
            (purchaseFilter === null || session.purchaseClicked === purchaseFilter) &&
            (confirmFilter === null || session.confirmClicked === confirmFilter) &&
            (evaluateYourselfFilter === null || hasEvaluateYourself === evaluateYourselfFilter) &&
            (lineJudgementFilter === null || hasLineJudgement === lineJudgementFilter) &&
            (matchToSimpleFilter === null || hasMatchToSimple === matchToSimpleFilter) &&
            (mathTestFilter === null || hasMathTest === mathTestFilter) &&
            (sudokuFilter === null || hasSudoku === sudokuFilter) &&
            (stroopFilter === null || hasStroop === stroopFilter) &&
            (corsiBlockFilter === null || hasCorsiBlock === corsiBlockFilter) &&
            (wordTestFilter === null || hasWordTest === wordTestFilter)
        );
    });

    const totalPages = Math.ceil(filteredSessions.length / USERS_PER_PAGE);
    const startIndex = (currentPage - 1) * USERS_PER_PAGE;
    const endIndex = startIndex + USERS_PER_PAGE;
    const paginatedSessions = filteredSessions.slice(startIndex, endIndex);

    // Grafik Verileri
    const stroopData = {
        labels: [t("correct"), t("incorrect")],
        datasets: [
            {
                label: t("stroop_answers"),
                data: [
                    filteredSessions
                        .flatMap((s) => (s.Stroop ? JSON.parse(s.Stroop).answers : []))
                        .filter((a: StroopAnswer) => a.correct).length,
                    filteredSessions
                        .flatMap((s) => (s.Stroop ? JSON.parse(s.Stroop).answers : []))
                        .filter((a: StroopAnswer) => !a.correct).length,
                ],
                backgroundColor: ["rgba(75, 192, 192, 0.7)", "rgba(255, 99, 132, 0.7)"],
                borderColor: ["rgba(75, 192, 192, 1)", "rgba(255, 99, 132, 1)"],
                borderWidth: 1,
                borderRadius: 4,
            },
        ],
    };

    const evaluateYourselfData = {
        labels: [t("verbal"), t("attention"), t("visual"), t("analytical"), t("memory")],
        datasets: [
            {
                label: t("evaluate_yourself"),
                data: [
                    (filteredSessions.reduce(
                        (sum, s) => sum + (s.EvaluateYourself ? JSON.parse(s.EvaluateYourself).VerbalIntelligence : 0),
                        0
                    ) / (filteredSessions.length || 1)).toFixed(2),
                    (filteredSessions.reduce(
                        (sum, s) => sum + (s.EvaluateYourself ? JSON.parse(s.EvaluateYourself).Attention : 0),
                        0
                    ) / (filteredSessions.length || 1)).toFixed(2),
                    (filteredSessions.reduce(
                        (sum, s) => sum + (s.EvaluateYourself ? JSON.parse(s.EvaluateYourself).VisualIntelligence : 0),
                        0
                    ) / (filteredSessions.length || 1)).toFixed(2),
                    (filteredSessions.reduce(
                        (sum, s) => sum + (s.EvaluateYourself ? JSON.parse(s.EvaluateYourself).AnalyticalIntelligence : 0),
                        0
                    ) / (filteredSessions.length || 1)).toFixed(2),
                    (filteredSessions.reduce(
                        (sum, s) => sum + (s.EvaluateYourself ? JSON.parse(s.EvaluateYourself).Memory : 0),
                        0
                    ) / (filteredSessions.length || 1)).toFixed(2),
                ],
                backgroundColor: "rgba(153, 102, 255, 0.7)",
                borderColor: "rgba(153, 102, 255, 1)",
                borderWidth: 1,
                borderRadius: 4,
            },
        ],
    };

    const generalStatsData = {
        labels: [t("total_users"), t("avg_session_min"), t("corsi_block_avg_xp"), t("word_test_accuracy")],
        datasets: [
            {
                label: t("general_stats"),
                data: [
                    filteredSessions.length,
                    filteredSessions.length > 0
                        ? (filteredSessions.reduce((sum, s) => sum + s.sessionTime, 0) / filteredSessions.length / 60).toFixed(2)
                        : 0,
                    filteredSessions.length > 0
                        ? (filteredSessions.reduce((sum, s) => sum + (s.CorsiBlock ? JSON.parse(s.CorsiBlock).totalXP : 0), 0) /
                            filteredSessions.length).toFixed(2)
                        : 0,
                    filteredSessions.length > 0
                        ? ((filteredSessions
                            .flatMap((s) => (s.WordTest ? JSON.parse(s.WordTest).results : []))
                            .filter((r: WordTestResult) => r.Correct).length /
                            filteredSessions.flatMap((s) => (s.WordTest ? JSON.parse(s.WordTest).results : [])).length) *
                            100).toFixed(2)
                        : 0,
                ],
                backgroundColor: [
                    "rgba(255, 206, 86, 0.7)",
                    "rgba(54, 162, 235, 0.7)",
                    "rgba(255, 159, 64, 0.7)",
                    "rgba(75, 192, 192, 0.7)",
                ],
                borderColor: [
                    "rgba(255, 206, 86, 1)",
                    "rgba(54, 162, 235, 1)",
                    "rgba(255, 159, 64, 1)",
                    "rgba(75, 192, 192, 1)",
                ],
                borderWidth: 1,
                borderRadius: 4,
            },
        ],
    };

    // Yeni Eklenen Grafik Verileri
    const purchaseData = {
        labels: [t("purchased"), t("not_purchased")],
        datasets: [
            {
                label: t("purchase"),
                data: [
                    filteredSessions.filter((s) => s.purchaseClicked).length,
                    filteredSessions.filter((s) => !s.purchaseClicked).length,
                ],
                backgroundColor: ["rgba(0, 172, 193, 0.7)", "rgba(255, 87, 34, 0.7)"],
                borderColor: ["rgba(0, 172, 193, 1)", "rgba(255, 87, 34, 1)"],
                borderWidth: 1,
                borderRadius: 4,
            },
        ],
    };

    const confirmData = {
        labels: [t("confirmed"), t("not_confirmed")],
        datasets: [
            {
                label: t("confirm"),
                data: [
                    filteredSessions.filter((s) => s.confirmClicked).length,
                    filteredSessions.filter((s) => !s.confirmClicked).length,
                ],
                backgroundColor: ["rgba(0, 172, 193, 0.7)", "rgba(255, 87, 34, 0.7)"],
                borderColor: ["rgba(0, 172, 193, 1)", "rgba(255, 87, 34, 1)"],
                borderWidth: 1,
                borderRadius: 4,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: "top" as const, labels: { font: { size: 12 } } },
            title: {
                display: true,
                font: { size: 16, weight: "600" as const },
                color: "#1f2937",
            },
            tooltip: {
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                titleFont: { size: 14 },
                bodyFont: { size: 12 },
            },
        },
        scales: {
            y: { beginAtZero: true, grid: { color: "rgba(0, 0, 0, 0.05)" } },
            x: { grid: { display: false } },
        },
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
    };

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    // Kullanıcı karakter analizi oluşturan fonksiyon
    const generateCharacterAnalysis = (session: any) => {
        const evaluateYourself: EvaluateYourself = session.EvaluateYourself
            ? JSON.parse(session.EvaluateYourself)
            : {};
        const lineJudgement: LineJudgement = session.LineJudgement
            ? JSON.parse(session.LineJudgement)
            : { questions: [] };
        const matchToSimple: MatchToSimpleGameManager = session.MatchToSimpleGameManager
            ? JSON.parse(session.MatchToSimpleGameManager)
            : { results: [] };
        const mathTest: MathTestQuestion[] = session.MathTest ? JSON.parse(session.MathTest) : [];
        const sudoku: SudokuGame[] = session.Sudoku ? JSON.parse(session.Sudoku) : [];
        const stroop: Stroop = session.Stroop
            ? JSON.parse(session.Stroop)
            : { answers: [], remainingLives: 0, totalScore: 0, maxCombo: 0 };
        const corsiBlock: CorsiBlock = session.CorsiBlock
            ? JSON.parse(session.CorsiBlock)
            : { totalPlayTime: 0, totalMoves: 0, totalXP: 0, results: [] };
        const wordTest: WordTest = session.WordTest
            ? JSON.parse(session.WordTest)
            : { results: [] };

        // Bilişsel yetenek analizi
        const cognitiveAnalysis = [];
        
        // Dikkat ve konsantrasyon analizi
        const stroopAccuracy = stroop.answers.length > 0 
            ? (stroop.answers.filter(a => a.correct).length / stroop.answers.length) * 100 
            : 0;
        
        if (stroopAccuracy > 80) {
            cognitiveAnalysis.push("yüksek dikkat ve konsantrasyon");
        } else if (stroopAccuracy > 60) {
            cognitiveAnalysis.push("orta düzeyde dikkat ve konsantrasyon");
        } else {
            cognitiveAnalysis.push("dikkat ve konsantrasyon konusunda zorlanma");
        }

        // Hafıza analizi
        const corsiBlockSuccess = corsiBlock.results.length > 0
            ? (corsiBlock.results.filter(r => r.isCorrect).length / corsiBlock.results.length) * 100
            : 0;
        
        if (corsiBlockSuccess > 70) {
            cognitiveAnalysis.push("güçlü hafıza");
        } else if (corsiBlockSuccess > 40) {
            cognitiveAnalysis.push("normal hafıza");
        } else {
            cognitiveAnalysis.push("hafıza konusunda zorlanma");
        }

        // Matematiksel yetenek analizi
        const mathAccuracy = mathTest.length > 0
            ? (mathTest.filter(q => q.IsCorrect).length / mathTest.length) * 100
            : 0;
        
        if (mathAccuracy > 80) {
            cognitiveAnalysis.push("güçlü matematiksel yetenek");
        } else if (mathAccuracy > 50) {
            cognitiveAnalysis.push("orta düzeyde matematiksel yetenek");
        } else {
            cognitiveAnalysis.push("matematiksel konularda zorlanma");
        }

        // Analitik düşünme analizi
        const sudokuCompletion = sudoku.length > 0
            ? (sudoku.filter(s => s.Completed).length / sudoku.length) * 100
            : 0;
        
        if (sudokuCompletion > 70) {
            cognitiveAnalysis.push("güçlü analitik düşünme");
        } else if (sudokuCompletion > 30) {
            cognitiveAnalysis.push("orta düzeyde analitik düşünme");
        } else {
            cognitiveAnalysis.push("analitik düşünme konusunda zorlanma");
        }

        // Sözel yetenek analizi
        const wordTestAccuracy = wordTest.results.length > 0
            ? (wordTest.results.filter(r => r.Correct).length / wordTest.results.length) * 100
            : 0;
        
        if (wordTestAccuracy > 80) {
            cognitiveAnalysis.push("güçlü sözel yetenek");
        } else if (wordTestAccuracy > 60) {
            cognitiveAnalysis.push("orta düzeyde sözel yetenek");
        } else {
            cognitiveAnalysis.push("sözel yetenek konusunda zorlanma");
        }

        // Davranışsal özellik analizi
        const behavioralAnalysis = [];
        
        // Motivasyon analizi
        if (session.sessionTime > 3600) { // 1 saatten fazla
            behavioralAnalysis.push("yüksek motivasyon");
        } else if (session.sessionTime > 1800) { // 30 dakikadan fazla
            behavioralAnalysis.push("orta düzeyde motivasyon");
        } else {
            behavioralAnalysis.push("düşük motivasyon");
        }

        // Tepki süresi analizi
        const avgResponseTime = stroop.answers.length > 0
            ? stroop.answers.reduce((sum, a) => sum + a.time, 0) / stroop.answers.length
            : 0;
        
        if (avgResponseTime < 1.5) {
            behavioralAnalysis.push("hızlı tepki süresi");
        } else if (avgResponseTime < 2.5) {
            behavioralAnalysis.push("normal tepki süresi");
        } else {
            behavioralAnalysis.push("yavaş tepki süresi");
        }

        // Karar verme davranışı
        if (session.purchaseClicked && session.confirmClicked) {
            behavioralAnalysis.push("kararlı ve güvenli karar verme");
        } else if (session.purchaseClicked && !session.confirmClicked) {
            behavioralAnalysis.push("dürtüsel karar verme");
        } else if (!session.purchaseClicked && session.confirmClicked) {
            behavioralAnalysis.push("tedbirli karar verme");
        } else {
            behavioralAnalysis.push("kararsız davranış");
        }

        // Kendini değerlendirme analizi
        const selfAssessment = [];
        
        if (Object.keys(evaluateYourself).length > 0) {
            const avgSelfRating = (
                evaluateYourself.VerbalIntelligence +
                evaluateYourself.Attention +
                evaluateYourself.VisualIntelligence +
                evaluateYourself.AnalyticalIntelligence +
                evaluateYourself.Memory
            ) / 5;
            
            if (avgSelfRating > 8) {
                selfAssessment.push("kendine güvenen");
            } else if (avgSelfRating > 6) {
                selfAssessment.push("kendini gerçekçi değerlendiren");
            } else {
                selfAssessment.push("kendini düşük değerlendiren");
            }
        }

        // Yaş aralığı tahmini
        let ageRange = "belirlenemiyor";
        
        // Yaş tahmini için daha kapsamlı faktörler
        // Tepki süresi, oyun tercihleri, zorluk seviyesi tercihleri, oyun süresi, hata oranları
        
        // Yaş puanı hesaplama (0-100 arası, düşük puan daha genç yaş grubunu gösterir)
        let ageScore = 50; // Başlangıç puanı
        
        // Tepki süresi faktörü (daha hızlı tepki = daha genç)
        if (avgResponseTime < 1.0) {
            ageScore -= 15; // Çok hızlı tepki
        } else if (avgResponseTime < 1.5) {
            ageScore -= 10; // Hızlı tepki
        } else if (avgResponseTime < 2.0) {
            ageScore -= 5; // Normal tepki
        } else if (avgResponseTime > 3.0) {
            ageScore += 15; // Çok yavaş tepki
        } else if (avgResponseTime > 2.5) {
            ageScore += 10; // Yavaş tepki
        }
        
        // Matematik yeteneği faktörü (daha yüksek = daha genç)
        if (mathAccuracy > 90) {
            ageScore -= 10;
        } else if (mathAccuracy > 80) {
            ageScore -= 5;
        } else if (mathAccuracy < 40) {
            ageScore += 10;
        } else if (mathAccuracy < 50) {
            ageScore += 5;
        }
        
        // Sözel yetenek faktörü (daha dengeli = daha yaşlı)
        const verbalMathDiff = Math.abs(wordTestAccuracy - mathAccuracy);
        if (verbalMathDiff < 10) {
            ageScore += 5; // Dengeli yetenekler genellikle daha yaşlı
        } else if (verbalMathDiff > 30) {
            ageScore -= 5; // Dengesiz yetenekler genellikle daha genç
        }
        
        // Hafıza faktörü (daha iyi hafıza = daha genç)
        if (corsiBlockSuccess > 80) {
            ageScore -= 10;
        } else if (corsiBlockSuccess > 70) {
            ageScore -= 5;
        } else if (corsiBlockSuccess < 40) {
            ageScore += 10;
        } else if (corsiBlockSuccess < 50) {
            ageScore += 5;
        }
        
        // Oyun süresi faktörü (daha uzun süre = daha genç)
        if (session.sessionTime > 7200) { // 2 saatten fazla
            ageScore -= 10;
        } else if (session.sessionTime > 3600) { // 1 saatten fazla
            ageScore -= 5;
        } else if (session.sessionTime < 900) { // 15 dakikadan az
            ageScore += 10;
        } else if (session.sessionTime < 1800) { // 30 dakikadan az
            ageScore += 5;
        }
        
        // Hata oranı faktörü (daha az hata = daha yaşlı)
        const errorRate = 1 - (stroop.answers.filter(a => a.correct).length / (stroop.answers.length || 1));
        if (errorRate < 0.1) {
            ageScore += 10; // Çok az hata
        } else if (errorRate < 0.2) {
            ageScore += 5; // Az hata
        } else if (errorRate > 0.4) {
            ageScore -= 10; // Çok fazla hata
        } else if (errorRate > 0.3) {
            ageScore -= 5; // Fazla hata
        }
        
        // Analitik düşünme faktörü (daha iyi = daha yaşlı)
        if (sudokuCompletion > 80) {
            ageScore += 5;
        } else if (sudokuCompletion < 30) {
            ageScore -= 5;
        }
        
        // Yaş aralığı belirleme
        if (ageScore < 20) {
            ageRange = "13-17 yaş arası";
        } else if (ageScore < 35) {
            ageRange = "18-25 yaş arası";
        } else if (ageScore < 50) {
            ageRange = "26-35 yaş arası";
        } else if (ageScore < 65) {
            ageRange = "36-45 yaş arası";
        } else if (ageScore < 80) {
            ageRange = "46-55 yaş arası";
        } else {
            ageRange = "56 yaş ve üzeri";
        }

        // Cinsiyet tahmini
        let genderPrediction = "belirlenemiyor";
        
        // Cinsiyet tahmini için daha kapsamlı faktörler
        // Matematik ve sözel yetenek dengesi, oyun tercihleri, tepki süreleri, hata oranları
        
        // Cinsiyet puanı hesaplama (-100 ile 100 arası, negatif değerler kadın, pozitif değerler erkek)
        let genderScore = 0; // Başlangıç puanı
        
        // Matematik-sözel yetenek dengesi (matematik > sözel = erkek, sözel > matematik = kadın)
        const mathVerbalDiff = mathAccuracy - wordTestAccuracy;
        genderScore += mathVerbalDiff * 0.5; // Her %1 fark için 0.5 puan
        
        // Tepki süresi faktörü (erkekler genellikle daha hızlı tepki verir)
        if (avgResponseTime < 1.2) {
            genderScore += 15;
        } else if (avgResponseTime < 1.5) {
            genderScore += 10;
        } else if (avgResponseTime < 1.8) {
            genderScore += 5;
        } else if (avgResponseTime > 2.5) {
            genderScore -= 15;
        } else if (avgResponseTime > 2.0) {
            genderScore -= 10;
        } else if (avgResponseTime > 1.8) {
            genderScore -= 5;
        }
        
        // Hafıza faktörü (kadınlar genellikle daha iyi hafızaya sahiptir)
        if (corsiBlockSuccess > 80) {
            genderScore -= 15;
        } else if (corsiBlockSuccess > 70) {
            genderScore -= 10;
        } else if (corsiBlockSuccess > 60) {
            genderScore -= 5;
        } else if (corsiBlockSuccess < 40) {
            genderScore += 15;
        } else if (corsiBlockSuccess < 50) {
            genderScore += 10;
        } else if (corsiBlockSuccess < 60) {
            genderScore += 5;
        }
        
        // Hata oranı faktörü (kadınlar genellikle daha az risk alır)
        if (errorRate < 0.1) {
            genderScore -= 10;
        } else if (errorRate < 0.2) {
            genderScore -= 5;
        } else if (errorRate > 0.4) {
            genderScore += 10;
        } else if (errorRate > 0.3) {
            genderScore += 5;
        }
        
        // Oyun süresi faktörü (erkekler genellikle daha uzun süre oynar)
        if (session.sessionTime > 7200) {
            genderScore += 15;
        } else if (session.sessionTime > 3600) {
            genderScore += 10;
        } else if (session.sessionTime > 1800) {
            genderScore += 5;
        } else if (session.sessionTime < 900) {
            genderScore -= 15;
        } else if (session.sessionTime < 1800) {
            genderScore -= 10;
        } else if (session.sessionTime < 2700) {
            genderScore -= 5;
        }
        
        // Analitik düşünme faktörü (erkekler genellikle daha iyi)
        if (sudokuCompletion > 80) {
            genderScore += 15;
        } else if (sudokuCompletion > 70) {
            genderScore += 10;
        } else if (sudokuCompletion > 60) {
            genderScore += 5;
        } else if (sudokuCompletion < 30) {
            genderScore -= 15;
        } else if (sudokuCompletion < 40) {
            genderScore -= 10;
        } else if (sudokuCompletion < 50) {
            genderScore -= 5;
        }
        
        // Satın alma davranışı faktörü (erkekler genellikle daha dürtüsel)
        if (session.purchaseClicked && !session.confirmClicked) {
            genderScore += 10;
        } else if (!session.purchaseClicked && session.confirmClicked) {
            genderScore -= 10;
        }
        
        // Cinsiyet tahmini belirleme
        if (genderScore > 30) {
            genderPrediction = "muhtemelen erkek (%" + Math.min(Math.round(Math.abs(genderScore)), 95) + " güven)";
        } else if (genderScore < -30) {
            genderPrediction = "muhtemelen kadın (%" + Math.min(Math.round(Math.abs(genderScore)), 95) + " güven)";
        } else if (genderScore > 10) {
            genderPrediction = "erkek olma olasılığı yüksek (%" + Math.min(Math.round(Math.abs(genderScore) + 20), 85) + " güven)";
        } else if (genderScore < -10) {
            genderPrediction = "kadın olma olasılığı yüksek (%" + Math.min(Math.round(Math.abs(genderScore) + 20), 85) + " güven)";
        } else {
            genderPrediction = "cinsiyet ayrımı yapmadan dengeli özellikler (belirsiz)";
        }

        // Karakter özellikleri analizi
        const personalityTraits = [];
        
        // Dışa dönüklük/içe dönüklük
        if (session.sessionTime > 3600 && stroop.answers.length > 50) {
            personalityTraits.push("dışa dönük");
        } else if (session.sessionTime < 1800 && stroop.answers.length < 30) {
            personalityTraits.push("içe dönük");
        } else {
            personalityTraits.push("dengeli sosyal yapı");
        }
        
        // Duygusal kararlılık
        if (stroop.answers.filter(a => a.correct).length / stroop.answers.length > 0.8) {
            personalityTraits.push("duygusal olarak kararlı");
        } else if (stroop.answers.filter(a => a.correct).length / stroop.answers.length < 0.5) {
            personalityTraits.push("duygusal olarak değişken");
        } else {
            personalityTraits.push("orta düzeyde duygusal kararlılık");
        }
        
        // Açıklık
        if (sudokuCompletion > 70 && corsiBlockSuccess > 70) {
            personalityTraits.push("yenilikçi ve açık fikirli");
        } else if (sudokuCompletion < 30 && corsiBlockSuccess < 30) {
            personalityTraits.push("geleneksel ve rutine bağlı");
        } else {
            personalityTraits.push("dengeli açıklık seviyesi");
        }
        
        // Sorumluluk
        if (session.confirmClicked && !session.purchaseClicked) {
            personalityTraits.push("sorumlu ve tedbirli");
        } else if (!session.confirmClicked && session.purchaseClicked) {
            personalityTraits.push("dürtüsel ve risk almaya açık");
        } else {
            personalityTraits.push("orta düzeyde sorumluluk");
        }
        
        // Uyumluluk
        if (wordTestAccuracy > 80 && mathAccuracy > 80) {
            personalityTraits.push("uyumlu ve işbirlikçi");
        } else if (wordTestAccuracy < 50 && mathAccuracy < 50) {
            personalityTraits.push("bağımsız ve rekabetçi");
        } else {
            personalityTraits.push("dengeli uyumluluk seviyesi");
        }

        // Ahlaki değerler analizi
        const moralValues = [];
        
        // Dürüstlük
        if (session.purchaseClicked && session.confirmClicked) {
            moralValues.push("dürüst ve şeffaf");
        } else if (!session.purchaseClicked && !session.confirmClicked) {
            moralValues.push("tedbirli ve güvenlik odaklı");
        } else {
            moralValues.push("dengeli ahlaki değerler");
        }
        
        // Empati
        if (wordTestAccuracy > 80 && corsiBlockSuccess > 80) {
            moralValues.push("yüksek empati yeteneği");
        } else if (wordTestAccuracy < 50 && corsiBlockSuccess < 50) {
            moralValues.push("empati konusunda geliştirilmesi gereken alanlar");
        } else {
            moralValues.push("orta düzeyde empati yeteneği");
        }
        
        // Adalet
        if (mathAccuracy > 80 && sudokuCompletion > 80) {
            moralValues.push("adil ve dengeli kararlar");
        } else if (mathAccuracy < 50 && sudokuCompletion < 50) {
            moralValues.push("karar verme konusunda zorlanma");
        } else {
            moralValues.push("orta düzeyde adalet anlayışı");
        }

        // Karakter analizi metni oluşturma
        let analysisText = `Bu kullanıcı, ${cognitiveAnalysis.join(", ")} özelliklerine sahip. `;
        
        if (behavioralAnalysis.length > 0) {
            analysisText += `Davranışsal olarak ${behavioralAnalysis.join(", ")} sergiliyor. `;
        }
        
        if (selfAssessment.length > 0) {
            analysisText += `Kendini değerlendirme açısından ${selfAssessment.join(", ")} bir profil çiziyor. `;
        }
        
        // Yaş ve cinsiyet tahmini
        analysisText += `Tahmini yaş aralığı: ${ageRange}. Cinsiyet tahmini: ${genderPrediction}. `;
        
        // Karakter özellikleri
        if (personalityTraits.length > 0) {
            analysisText += `Karakter özellikleri: ${personalityTraits.join(", ")}. `;
        }
        
        // Ahlaki değerler
        if (moralValues.length > 0) {
            analysisText += `Ahlaki değerler: ${moralValues.join(", ")}. `;
        }
        
        // Genel performans değerlendirmesi
        const overallPerformance = (stroopAccuracy + corsiBlockSuccess + mathAccuracy + sudokuCompletion + wordTestAccuracy) / 5;
        
        if (overallPerformance > 80) {
            analysisText += "Genel olarak oyunlarda çok başarılı bir performans gösteriyor.";
        } else if (overallPerformance > 60) {
            analysisText += "Genel olarak oyunlarda iyi bir performans gösteriyor.";
        } else if (overallPerformance > 40) {
            analysisText += "Genel olarak oyunlarda orta düzeyde bir performans gösteriyor.";
        } else {
            analysisText += "Genel olarak oyunlarda geliştirilmesi gereken bir performans gösteriyor.";
        }

        return analysisText;
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <Navbar />
            <main className="mt-36 mb-16 flex-grow max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="mb-8 flex items-center justify-between"
                >
                    <div className="flex items-center space-x-4">
                        <img src={gameDetails["game6"]?.logo || ""} alt="Genix" className="w-12 h-12 rounded-full" />
                        <h2 className="text-3xl font-bold text-gray-800">Genix {t("users")}</h2>
                    </div>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleBack}
                        className="bg-gray-200 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-300 transition-all duration-200"
                    >
                        {t("back_to_dashboard")}
                    </motion.button>
                </motion.div>

                {/* Grafik Bölümü */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="bg-white rounded-xl shadow-lg p-5"
                    >
                        <Bar
                            data={stroopData}
                            options={{
                                ...chartOptions,
                                plugins: { ...chartOptions.plugins, title: { ...chartOptions.plugins.title, text: t("stroop_accuracy"), font: { size: 16, weight: "bold" }, color: "#1f2937" } },
                            }}
                            height={200}
                        />
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="bg-white rounded-xl shadow-lg p-5"
                    >
                        <Bar
                            data={evaluateYourselfData}
                            options={{
                                ...chartOptions,
                                plugins: { ...chartOptions.plugins, title: { ...chartOptions.plugins.title, text: t("evaluate_yourself_avg"), font: { size: 16, weight: "bold" }, color: "#1f2937" } },
                            }}
                            height={200}
                        />
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                        className="bg-white rounded-xl shadow-lg p-5"
                    >
                        <Bar
                            data={generalStatsData}
                            options={{
                                ...chartOptions,
                                plugins: { ...chartOptions.plugins, title: { ...chartOptions.plugins.title, text: t("general_stats"), font: { size: 16, weight: "bold" }, color: "#1f2937" } },
                            }}
                            height={200}
                        />
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        className="bg-white rounded-xl shadow-lg p-5"
                    >
                        <Bar
                            data={purchaseData}
                            options={{
                                ...chartOptions,
                                plugins: { ...chartOptions.plugins, title: { ...chartOptions.plugins.title, text: t("purchase_stats"), font: { size: 16, weight: "bold" }, color: "#1f2937" } },
                            }}
                            height={200}
                        />
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.5 }}
                        className="bg-white rounded-xl shadow-lg p-5"
                    >
                        <Bar
                            data={confirmData}
                            options={{
                                ...chartOptions,
                                plugins: { ...chartOptions.plugins, title: { ...chartOptions.plugins.title, text: t("confirm_stats"), font: { size: 16, weight: "bold" }, color: "#1f2937" } },
                            }}
                            height={200}
                        />
                    </motion.div>
                </div>

                {/* Filtreleme Bölümü */}
                <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4">{t("filters")}</h3>
                    
                    {/* Temel Filtreler */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t("user_id")}</label>
                            <input
                                type="text"
                                value={userIdFilter}
                                onChange={(e) => setUserIdFilter(e.target.value)}
                                placeholder={t("eg_user_id")}
                                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t("purchase_status")}</label>
                            <select
                                value={purchaseFilter === null ? "" : purchaseFilter ? "true" : "false"}
                                onChange={(e) => setPurchaseFilter(e.target.value === "" ? null : e.target.value === "true")}
                                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">{t("all")}</option>
                                <option value="true">{t("purchased")}</option>
                                <option value="false">{t("not_purchased")}</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t("confirm_status")}</label>
                            <select
                                value={confirmFilter === null ? "" : confirmFilter ? "true" : "false"}
                                onChange={(e) => setConfirmFilter(e.target.value === "" ? null : e.target.value === "true")}
                                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">{t("all")}</option>
                                <option value="true">{t("confirmed")}</option>
                                <option value="false">{t("not_confirmed")}</option>
                            </select>
                        </div>
                    </div>
                    
                    {/* Test Filtreleri */}
                    <h4 className="text-lg font-medium text-gray-700 mb-3">{t("test_filters")}</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t("evaluate_yourself")}</label>
                            <select
                                value={evaluateYourselfFilter === null ? "" : evaluateYourselfFilter ? "true" : "false"}
                                onChange={(e) => setEvaluateYourselfFilter(e.target.value === "" ? null : e.target.value === "true")}
                                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">{t("all")}</option>
                                <option value="true">{t("completed")}</option>
                                <option value="false">{t("not_completed")}</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t("line_judgement")}</label>
                            <select
                                value={lineJudgementFilter === null ? "" : lineJudgementFilter ? "true" : "false"}
                                onChange={(e) => setLineJudgementFilter(e.target.value === "" ? null : e.target.value === "true")}
                                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">{t("all")}</option>
                                <option value="true">{t("completed")}</option>
                                <option value="false">{t("not_completed")}</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t("match_to_simple")}</label>
                            <select
                                value={matchToSimpleFilter === null ? "" : matchToSimpleFilter ? "true" : "false"}
                                onChange={(e) => setMatchToSimpleFilter(e.target.value === "" ? null : e.target.value === "true")}
                                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">{t("all")}</option>
                                <option value="true">{t("completed")}</option>
                                <option value="false">{t("not_completed")}</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t("math_test")}</label>
                            <select
                                value={mathTestFilter === null ? "" : mathTestFilter ? "true" : "false"}
                                onChange={(e) => setMathTestFilter(e.target.value === "" ? null : e.target.value === "true")}
                                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">{t("all")}</option>
                                <option value="true">{t("completed")}</option>
                                <option value="false">{t("not_completed")}</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t("sudoku")}</label>
                            <select
                                value={sudokuFilter === null ? "" : sudokuFilter ? "true" : "false"}
                                onChange={(e) => setSudokuFilter(e.target.value === "" ? null : e.target.value === "true")}
                                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">{t("all")}</option>
                                <option value="true">{t("completed")}</option>
                                <option value="false">{t("not_completed")}</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t("stroop_test")}</label>
                            <select
                                value={stroopFilter === null ? "" : stroopFilter ? "true" : "false"}
                                onChange={(e) => setStroopFilter(e.target.value === "" ? null : e.target.value === "true")}
                                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">{t("all")}</option>
                                <option value="true">{t("completed")}</option>
                                <option value="false">{t("not_completed")}</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t("corsi_block")}</label>
                            <select
                                value={corsiBlockFilter === null ? "" : corsiBlockFilter ? "true" : "false"}
                                onChange={(e) => setCorsiBlockFilter(e.target.value === "" ? null : e.target.value === "true")}
                                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">{t("all")}</option>
                                <option value="true">{t("completed")}</option>
                                <option value="false">{t("not_completed")}</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t("word_test")}</label>
                            <select
                                value={wordTestFilter === null ? "" : wordTestFilter ? "true" : "false"}
                                onChange={(e) => setWordTestFilter(e.target.value === "" ? null : e.target.value === "true")}
                                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">{t("all")}</option>
                                <option value="true">{t("completed")}</option>
                                <option value="false">{t("not_completed")}</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Kullanıcı Kartları */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {paginatedSessions.length > 0 ? (
                        paginatedSessions.map((session) => {
                            const evaluateYourself: EvaluateYourself = session.EvaluateYourself
                                ? JSON.parse(session.EvaluateYourself)
                                : {};
                            const lineJudgement: LineJudgement = session.LineJudgement
                                ? JSON.parse(session.LineJudgement)
                                : { questions: [] };
                            const matchToSimple: MatchToSimpleGameManager = session.MatchToSimpleGameManager
                                ? JSON.parse(session.MatchToSimpleGameManager)
                                : { results: [] };
                            const mathTest: MathTestQuestion[] = session.MathTest ? JSON.parse(session.MathTest) : [];
                            const sudoku: SudokuGame[] = session.Sudoku ? JSON.parse(session.Sudoku) : [];
                            const stroop: Stroop = session.Stroop
                                ? JSON.parse(session.Stroop)
                                : { answers: [], remainingLives: 0, totalScore: 0, maxCombo: 0 };
                            const corsiBlock: CorsiBlock = session.CorsiBlock
                                ? JSON.parse(session.CorsiBlock)
                                : { totalPlayTime: 0, totalMoves: 0, totalXP: 0, results: [] };
                            const wordTest: WordTest = session.WordTest
                                ? JSON.parse(session.WordTest)
                                : { results: [] };

                            return (
                                <motion.div
                                    key={session.userId}
                                    className="bg-white rounded-xl shadow-lg p-5 hover:shadow-xl transition-shadow duration-200"
                                    variants={cardVariants}
                                    initial="hidden"
                                    animate="visible"
                                >
                                    <h3 className="text-lg font-semibold text-gray-800 mb-3">
                                        {" "}
                                        <span className="relative group">
                                            <span className="text-gray-800">#</span>
                                            <span className="absolute left-full ml-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-gray-100 px-2 py-1 rounded text-sm text-gray-700">
                                                {session.userId}
                                            </span>
                                        </span>
                                    </h3>
                                    <div className="space-y-2 text-gray-600">
                                        <p>
                                            <span className="font-medium text-blue-600">{t("session_time")}:</span>{" "}
                                            {formatSessionTime(session.sessionTime)}
                                        </p>
                                        <p>
                                            <span className="font-medium text-red-600">{t("max_level")}:</span> {session.maxLevel}
                                        </p>
                                        <p>
                                            <span className="font-medium text-green-600">{t("confirm_clicked")}:</span>{" "}
                                            {session.confirmClicked ? t("yes") : t("no")}
                                        </p>
                                        <p>
                                            <span className="font-medium text-purple-600">{t("purchase_clicked")}:</span>{" "}
                                            {session.purchaseClicked ? t("yes") : t("no")}
                                        </p>
                                    </div>

                                    {/* Accordion Yapısı */}
                                    <div className="mt-4 space-y-2">
                                        {/* Evaluate Yourself Accordion */}
                                        <div className="border rounded-lg overflow-hidden">
                                            <button 
                                                className="w-full flex justify-between items-center p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                                                onClick={() => {
                                                    const element = document.getElementById(`evaluate-yourself-${session.userId}`);
                                                    if (element) {
                                                        element.classList.toggle('hidden');
                                                    }
                                                }}
                                            >
                                                <div className="flex items-center">
                                                    <h4 className="text-md font-semibold text-gray-700">{t("evaluate_yourself")}</h4>
                                                    <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${
                                                        Object.keys(evaluateYourself).length > 0 
                                                        ? "bg-green-100 text-green-800" 
                                                        : "bg-red-100 text-red-800"
                                                    }`}>
                                                        {Object.keys(evaluateYourself).length > 0 ? t("data_available") : t("no_data_available")}
                                                    </span>
                                                </div>
                                                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                                </svg>
                                            </button>
                                            <div id={`evaluate-yourself-${session.userId}`} className="hidden p-3 bg-white">
                                                {Object.keys(evaluateYourself).length > 0 ? (
                                                    <ul className="space-y-1 text-gray-600">
                                                        <li>
                                                            <span className="font-medium">{t("verbal_intelligence")}:</span>{" "}
                                                            {evaluateYourself.VerbalIntelligence.toFixed(2)}
                                                        </li>
                                                        <li>
                                                            <span className="font-medium">{t("attention")}:</span> {evaluateYourself.Attention.toFixed(2)}
                                                        </li>
                                                        <li>
                                                            <span className="font-medium">{t("visual_intelligence")}:</span>{" "}
                                                            {evaluateYourself.VisualIntelligence.toFixed(2)}
                                                        </li>
                                                        <li>
                                                            <span className="font-medium">{t("analytical_intelligence")}:</span>{" "}
                                                            {evaluateYourself.AnalyticalIntelligence.toFixed(2)}
                                                        </li>
                                                        <li>
                                                            <span className="font-medium">{t("memory")}:</span> {evaluateYourself.Memory.toFixed(2)}
                                                        </li>
                                                    </ul>
                                                ) : (
                                                    <p className="text-gray-500">{t("no_data_available")}</p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Line Judgement Accordion */}
                                        <div className="border rounded-lg overflow-hidden">
                                            <button 
                                                className="w-full flex justify-between items-center p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                                                onClick={() => {
                                                    const element = document.getElementById(`line-judgement-${session.userId}`);
                                                    if (element) {
                                                        element.classList.toggle('hidden');
                                                    }
                                                }}
                                            >
                                                <div className="flex items-center">
                                                    <h4 className="text-md font-semibold text-gray-700">{t("line_judgement")}</h4>
                                                    <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${
                                                        lineJudgement.questions.length > 0 
                                                        ? "bg-green-100 text-green-800" 
                                                        : "bg-red-100 text-red-800"
                                                    }`}>
                                                        {lineJudgement.questions.length > 0 ? t("data_available") : t("no_data_available")}
                                                    </span>
                                                </div>
                                                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                                </svg>
                                            </button>
                                            <div id={`line-judgement-${session.userId}`} className="hidden p-3 bg-white">
                                                {lineJudgement.questions.length > 0 ? (
                                                    <div className="overflow-x-auto">
                                                        <table className="w-full text-sm text-gray-600">
                                                            <thead className="bg-gray-100">
                                                                <tr>
                                                                    <th className="py-2 px-3 text-left font-medium">{t("left_line")}</th>
                                                                    <th className="py-2 px-3 text-left font-medium">{t("right_line")}</th>
                                                                    <th className="py-2 px-3 text-left font-medium">{t("chosen")}</th>
                                                                    <th className="py-2 px-3 text-left font-medium">{t("correct")}</th>
                                                                    <th className="py-2 px-3 text-left font-medium">{t("response_time")}</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {lineJudgement.questions.map((q, index) => (
                                                                    <tr key={index} className="border-b">
                                                                        <td className="py-2 px-3">{q.leftLineLength}</td>
                                                                        <td className="py-2 px-3">{q.rightLineLength}</td>
                                                                        <td className="py-2 px-3">{q.chosenIndex === 0 ? t("left") : t("right")}</td>
                                                                        <td className="py-2 px-3">{q.isCorrect ? t("yes") : t("no")}</td>
                                                                        <td className="py-2 px-3">{q.responseTime.toFixed(2)}{t("s")}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ) : (
                                                    <p className="text-gray-500">{t("no_data_available")}</p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Match To Simple Accordion */}
                                        <div className="border rounded-lg overflow-hidden">
                                            <button 
                                                className="w-full flex justify-between items-center p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                                                onClick={() => {
                                                    const element = document.getElementById(`match-to-simple-${session.userId}`);
                                                    if (element) {
                                                        element.classList.toggle('hidden');
                                                    }
                                                }}
                                            >
                                                <div className="flex items-center">
                                                    <h4 className="text-md font-semibold text-gray-700">{t("match_to_simple")}</h4>
                                                    <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${
                                                        matchToSimple.results.length > 0 
                                                        ? "bg-green-100 text-green-800" 
                                                        : "bg-red-100 text-red-800"
                                                    }`}>
                                                        {matchToSimple.results.length > 0 ? t("data_available") :t("no_data_available")}
                                                    </span>
                                                </div>
                                                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                                </svg>
                                            </button>
                                            <div id={`match-to-simple-${session.userId}`} className="hidden p-3 bg-white">
                                                {matchToSimple.results.length > 0 ? (
                                                    <div className="overflow-x-auto">
                                                        <table className="w-full text-sm text-gray-600">
                                                            <thead className="bg-gray-100">
                                                                <tr>
                                                                    <th className="py-2 px-3 text-left font-medium">{t("correct")}</th>
                                                                    <th className="py-2 px-3 text-left font-medium">{t("response_time")}</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {matchToSimple.results.map((r, index) => (
                                                                    <tr key={index} className="border-b">
                                                                        <td className="py-2 px-3">{r.isCorrect ? t("yes") : t("no")}</td>
                                                                        <td className="py-2 px-3">{r.responseTime.toFixed(2)}{t("s")}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ) : (
                                                    <p className="text-gray-500">{t("no_data_available")}</p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Math Test Accordion */}
                                        <div className="border rounded-lg overflow-hidden">
                                            <button 
                                                className="w-full flex justify-between items-center p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                                                onClick={() => {
                                                    const element = document.getElementById(`math-test-${session.userId}`);
                                                    if (element) {
                                                        element.classList.toggle('hidden');
                                                    }
                                                }}
                                            >
                                                <div className="flex items-center">
                                                    <h4 className="text-md font-semibold text-gray-700">{t("math_test")}</h4>
                                                    <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${
                                                        mathTest.length > 0 
                                                        ? "bg-green-100 text-green-800" 
                                                        : "bg-red-100 text-red-800"
                                                    }`}>
                                                        {mathTest.length > 0 ?t("data_available") : t("no_data_available")}
                                                    </span>
                                                </div>
                                                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                                </svg>
                                            </button>
                                            <div id={`math-test-${session.userId}`} className="hidden p-3 bg-white">
                                                {mathTest.length > 0 ? (
                                                    <div className="overflow-x-auto">
                                                        <table className="w-full text-sm text-gray-600">
                                                            <thead className="bg-gray-100">
                                                                <tr>
                                                                    <th className="py-2 px-3 text-left font-medium">{t("options")}</th>
                                                                    <th className="py-2 px-3 text-left font-medium">{t("selected_answer")}</th>
                                                                    <th className="py-2 px-3 text-left font-medium">{t("correct")}</th>
                                                                    <th className="py-2 px-3 text-left font-medium">{t("time_taken")}</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {mathTest.map((q, index) => (
                                                                    <tr key={index} className="border-b">
                                                                        <td className="py-2 px-3">{q.Options.join(", ")}</td>
                                                                        <td className="py-2 px-3">{q.SelectedAnswer}</td>
                                                                        <td className="py-2 px-3">{q.IsCorrect ? t("yes") : t("no")}</td>
                                                                        <td className="py-2 px-3">{q.TimeTaken.toFixed(2)}{t("s")}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ) : (
                                                    <p className="text-gray-500">{t("no_data_available")}</p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Sudoku Accordion */}
                                        <div className="border rounded-lg overflow-hidden">
                                            <button 
                                                className="w-full flex justify-between items-center p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                                                onClick={() => {
                                                    const element = document.getElementById(`sudoku-${session.userId}`);
                                                    if (element) {
                                                        element.classList.toggle('hidden');
                                                    }
                                                }}
                                            >
                                                <div className="flex items-center">
                                                    <h4 className="text-md font-semibold text-gray-700">{t("sudoku")}</h4>
                                                    <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${
                                                        sudoku.length > 0 
                                                        ? "bg-green-100 text-green-800" 
                                                        : "bg-red-100 text-red-800"
                                                    }`}>
                                                        {sudoku.length > 0 ?t("data_available") :t("no_data_available")}
                                                    </span>
                                                </div>
                                                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                                </svg>
                                            </button>
                                            <div id={`sudoku-${session.userId}`} className="hidden p-3 bg-white">
                                                {sudoku.length > 0 ? (
                                                    <div className="overflow-x-auto">
                                                        <table className="w-full text-sm text-gray-600">
                                                            <thead className="bg-gray-100">
                                                                <tr>
                                                                    <th className="py-2 px-3 text-left font-medium">{t("difficulty")}</th>
                                                                    <th className="py-2 px-3 text-left font-medium">{t("total_time")}</th>
                                                                    <th className="py-2 px-3 text-left font-medium">{t("move_count")}</th>
                                                                    <th className="py-2 px-3 text-left font-medium">{t("completed")}</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {sudoku.map((s, index) => (
                                                                    <tr key={index} className="border-b">
                                                                        <td className="py-2 px-3">{s.Difficulty}</td>
                                                                        <td className="py-2 px-3">{s.TotalTime.toFixed(2)}{t("s")}</td>
                                                                        <td className="py-2 px-3">{s.MoveCount}</td>
                                                                        <td className="py-2 px-3">{s.Completed ? t("yes") : t("no")}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ) : (
                                                    <p className="text-gray-500">{t("no_data_available")}</p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Stroop Accordion */}
                                        <div className="border rounded-lg overflow-hidden">
                                            <button 
                                                className="w-full flex justify-between items-center p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                                                onClick={() => {
                                                    const element = document.getElementById(`stroop-${session.userId}`);
                                                    if (element) {
                                                        element.classList.toggle('hidden');
                                                    }
                                                }}
                                            >
                                                <div className="flex items-center">
                                                    <h4 className="text-md font-semibold text-gray-700">{t("stroop_test")}</h4>
                                                    <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${
                                                        stroop.answers.length > 0 
                                                        ? "bg-green-100 text-green-800" 
                                                        : "bg-red-100 text-red-800"
                                                    }`}>
                                                        {stroop.answers.length > 0 ? t("data_available") :t("no_data_available")}
                                                    </span>
                                                </div>
                                                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                                </svg>
                                            </button>
                                            <div id={`stroop-${session.userId}`} className="hidden p-3 bg-white">
                                                {stroop.answers.length > 0 ? (
                                                    <div className="space-y-4">
                                                        <div className="overflow-x-auto">
                                                            <table className="w-full text-sm text-gray-600">
                                                                <thead className="bg-gray-100">
                                                                    <tr>
                                                                        <th className="py-2 px-3 text-left font-medium">{t("question")}</th>
                                                                        <th className="py-2 px-3 text-left font-medium">{t("text_color")}</th>
                                                                        <th className="py-2 px-3 text-left font-medium">{t("selected")}</th>
                                                                        <th className="py-2 px-3 text-left font-medium">{t("correct")}</th>
                                                                        <th className="py-2 px-3 text-left font-medium">{t("time")}</th>
                                                                        <th className="py-2 px-3 text-left font-medium">{t("score")}</th>
                                                                        <th className="py-2 px-3 text-left font-medium">{t("combo")}</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {stroop.answers.map((a, index) => (
                                                                        <tr key={index} className="border-b">
                                                                            <td className="py-2 px-3">{a.question}</td>
                                                                            <td className="py-2 px-3">{a.textColor}</td>
                                                                            <td className="py-2 px-3">{a.selected}</td>
                                                                            <td className="py-2 px-3">{a.correct ? t("yes") : t("no")}</td>
                                                                            <td className="py-2 px-3">{a.time.toFixed(2)}{t("s")}</td>
                                                                            <td className="py-2 px-3">{a.scoreEarned}</td>
                                                                            <td className="py-2 px-3">{a.comboMultiplier}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                        <div className="text-gray-600">
                                                            <p>
                                                                <span className="font-medium">{t("remaining_lives")}:</span> {stroop.remainingLives}
                                                            </p>
                                                            <p>
                                                                <span className="font-medium">{t("total_score")}:</span> {stroop.totalScore}
                                                            </p>
                                                            <p>
                                                                <span className="font-medium">{t("max_combo")}:</span> {stroop.maxCombo}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <p className="text-gray-500">{t("no_data_available")}</p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Corsi Block Accordion */}
                                        <div className="border rounded-lg overflow-hidden">
                                            <button 
                                                className="w-full flex justify-between items-center p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                                                onClick={() => {
                                                    const element = document.getElementById(`corsi-block-${session.userId}`);
                                                    if (element) {
                                                        element.classList.toggle('hidden');
                                                    }
                                                }}
                                            >
                                                <div className="flex items-center">
                                                    <h4 className="text-md font-semibold text-gray-700">{t("corsi_block")}</h4>
                                                    <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${
                                                        corsiBlock.results.length > 0 
                                                        ? "bg-green-100 text-green-800" 
                                                        : "bg-red-100 text-red-800"
                                                    }`}>
                                                        {corsiBlock.results.length > 0 ? t("data_available") : t("no_data_available")}
                                                    </span>
                                                </div>
                                                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                                </svg>
                                            </button>
                                            <div id={`corsi-block-${session.userId}`} className="hidden p-3 bg-white">
                                                {corsiBlock.results.length > 0 ? (
                                                    <div className="space-y-4">
                                                        <div className="overflow-x-auto">
                                                            <table className="w-full text-sm text-gray-600">
                                                                <thead className="bg-gray-100">
                                                                    <tr>
                                                                        <th className="py-2 px-3 text-left font-medium">{t("level")}</th>
                                                                        <th className="py-2 px-3 text-left font-medium">{t("sequence_length")}</th>
                                                                        <th className="py-2 px-3 text-left font-medium">{t("time_taken")}</th>
                                                                        <th className="py-2 px-3 text-left font-medium">{t("correct")}</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {corsiBlock.results.map((r, index) => (
                                                                        <tr key={index} className="border-b">
                                                                            <td className="py-2 px-3">{r.level}</td>
                                                                            <td className="py-2 px-3">{r.sequenceLength}</td>
                                                                            <td className="py-2 px-3">{r.timeTaken.toFixed(2)}{t("s")}</td>
                                                                            <td className="py-2 px-3">{r.isCorrect ? t("yes") : t("no")}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                        <div className="text-gray-600">
                                                            <p>
                                                                <span className="font-medium">{t("total_play_time")}:</span>{" "}
                                                                {corsiBlock.totalPlayTime.toFixed(2)}{t("s")}
                                                            </p>
                                                            <p>
                                                                <span className="font-medium">{t("total_moves")}:</span> {corsiBlock.totalMoves}
                                                            </p>
                                                            <p>
                                                                <span className="font-medium">{t("total_xp")}:</span> {corsiBlock.totalXP}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <p className="text-gray-500">{t("no_data_available")}</p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Word Test Accordion */}
                                        <div className="border rounded-lg overflow-hidden">
                                            <button 
                                                className="w-full flex justify-between items-center p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                                                onClick={() => {
                                                    const element = document.getElementById(`word-test-${session.userId}`);
                                                    if (element) {
                                                        element.classList.toggle('hidden');
                                                    }
                                                }}
                                            >
                                                <div className="flex items-center">
                                                    <h4 className="text-md font-semibold text-gray-700">{t("word_test")}</h4>
                                                    <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${
                                                        wordTest.results.length > 0 
                                                        ? "bg-green-100 text-green-800" 
                                                        : "bg-red-100 text-red-800"
                                                    }`}>
                                                        {wordTest.results.length > 0 ? t("data_available") : t("no_data_available")}
                                                    </span>
                                                </div>
                                                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                                </svg>
                                            </button>
                                            <div id={`word-test-${session.userId}`} className="hidden p-3 bg-white">
                                                {wordTest.results.length > 0 ? (
                                                    <div className="overflow-x-auto">
                                                        <table className="w-full text-sm text-gray-600">
                                                            <thead className="bg-gray-100">
                                                                <tr>
                                                                    <th className="py-2 px-3 text-left font-medium">{t("question_index")}</th>
                                                                    <th className="py-2 px-3 text-left font-medium">{t("question")}</th>
                                                                    <th className="py-2 px-3 text-left font-medium">{t("time_taken")}</th>
                                                                    <th className="py-2 px-3 text-left font-medium">{t("correct")}</th>
                                                                    <th className="py-2 px-3 text-left font-medium">{t("combo")}</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {wordTest.results.map((r, index) => (
                                                                    <tr key={index} className="border-b">
                                                                        <td className="py-2 px-3">{r.questionIndex}</td>
                                                                        <td className="py-2 px-3">{r.question}</td>
                                                                        <td className="py-2 px-3">{r.timeTaken.toFixed(2)}{t("s")}</td>
                                                                        <td className="py-2 px-3">{r.Correct ? t("yes") : t("no")}</td>
                                                                        <td className="py-2 px-3">{r.comboMultiplier}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ) : (
                                                    <p className="text-gray-500">{t("no_data_available")}</p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Karakter Analizi Accordion */}
                                        <div className="border rounded-lg overflow-hidden">
                                            <button 
                                                className="w-full flex justify-between items-center p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                                                onClick={() => {
                                                    const element = document.getElementById(`character-analysis-${session.userId}`);
                                                    if (element) {
                                                        element.classList.toggle('hidden');
                                                    }
                                                }}
                                            >
                                                <div className="flex items-center">
                                                    <h4 className="text-md font-semibold text-gray-700">{t("character_analysis")}</h4>
                                                    <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                                        Analiz
                                                    </span>
                                                </div>
                                                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                                </svg>
                                            </button>
                                            <div id={`character-analysis-${session.userId}`} className="hidden p-3 bg-white">
                                                <div className="bg-gray-50 p-4 rounded-lg">
                                                    <p className="text-gray-700 text-sm leading-relaxed">
                                                        {generateCharacterAnalysis(session)}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })
                    ) : (
                        <p className="col-span-full text-gray-500 text-center py-8">{t("no_user_data", { gameName: "Genix" })}</p>
                    )}
                </div>

                {/* Pagination Kontrolleri */}
                {totalPages > 1 && (
                    <div className="mt-8 flex items-center justify-center space-x-4">
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={`py-2 px-4 rounded-lg font-medium transition-all duration-200 ${currentPage === 1
                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                : "bg-blue-500 text-white hover:bg-blue-600"
                                }`}
                        >
                            {t("previous")}
                        </motion.button>
                        <span className="text-gray-700">
                            {t("page")} {currentPage} / {totalPages}
                        </span>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className={`py-2 px-4 rounded-lg font-medium transition-all duration-200 ${currentPage === totalPages
                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                : "bg-blue-500 text-white hover:bg-blue-600"
                                }`}
                        >
                            {t("next")}
                        </motion.button>
                    </div>
                )}
            </main>

            <footer className="bg-gray-800 py-4">
                <div className="max-w-7xl mx-auto px-4 text-center">
                    <p className="text-gray-300 text-sm">{t("footer", { year: new Date().getFullYear() })}</p>
                </div>
            </footer>
        </div>
    );
};

export default UsersPageGenix;